import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { StaticInfoSearchService } from '@apps/main/modules/search/staticInfo/staticInfoSearchService';
import { useItineraryStore } from '@apps/main/new-desktop/core/stores';

import { assertValue } from '@libs/utils';
import type {
  FerryIngredientResponse,
  FerryTicketCategory,
  IngredientId,
} from '@libs/trip-planner-interfaces';

import type { TokensForReplacement } from '../../models';

export const useUpsellStore = defineStore('UpsellStore', () => {
  const staticInfoSearchService = new StaticInfoSearchService();
  const itineraryStore = useItineraryStore();

  const tokenListForAddons = computed<string[]>(() => {
    const acc: string[] = [];
    if (!itineraryStore.selectedItinerary) {
      return acc;
    }

    return itineraryStore.selectedItinerary.ferryOnlyItinerary.reduce(
      (prev: string[], current: FerryIngredientResponse) => {
        if (current.addToBasketToken !== null) {
          prev.push(current.addToBasketToken);
        }
        return prev;
      },

      acc
    );
  });
  // FERRY TICKET CATEGORIES
  // Consider moving to a separate store when more upsell categories are added

  // STATE
  const ferryTicketCategoryDictionary = ref<
    Record<string, FerryTicketCategory[]>
  >({});

  const userSelectedFerryTicketCategory = ref<
    Record<string, FerryTicketCategory>
  >({});

  const prevousSelectedTicketCategory = ref<Record<string, string>>({});

  const currentFerryTicketCategoryEditionKey = ref<string>('');
  const currentFerryTicketEditionIngredientId = ref<string | undefined>();

  const isLoadingDictionary = ref<Set<string>>(new Set());

  // ACTIONS

  function setTicketCategoryToDictionary(
    key: string,
    ticketCategories: FerryTicketCategory[]
  ): void {
    // INFO: cheapest should be always same as base product.
    ferryTicketCategoryDictionary.value[key] = ticketCategories.sort(
      (a: FerryTicketCategory, b: FerryTicketCategory) =>
        a.price.amount - b.price.amount
    );
  }

  function fetchTicketCategories(
    key: string,
    ingredientId: IngredientId
  ): void {
    // INFO: To reduce number of requests, we are fetching ferry ticket categories only once.
    if (!ferryTicketCategoryDictionary.value[key]) {
      isLoadingDictionary.value.add(ingredientId);

      staticInfoSearchService
        .getFerryTicketCategories(key)
        .subscribe((ticketCategories) => {
          setTicketCategoryToDictionary(key, ticketCategories);
          const previousTicket =
            prevousSelectedTicketCategory.value[ingredientId];
          if (previousTicket !== undefined) {
            currentFerryTicketCategoryEditionKey.value = key;
            setUserSelectionFromModal(previousTicket);
          }
          isLoadingDictionary.value.delete(ingredientId);
        });
    }
    // set from cache ticket category
  }

  function getTicketCategories(key: string): FerryTicketCategory[] {
    const ferryTicketCategories = ferryTicketCategoryDictionary.value[key];

    if (!ferryTicketCategories) {
      return [];
    }

    return ferryTicketCategories;
  }

  function setUserSelectedFerryTicketCategory(
    key: string,
    ticketCategory: FerryTicketCategory
  ): void {
    userSelectedFerryTicketCategory.value[key] = ticketCategory;
  }

  function setUserSelectionFromModal(category: string): void {
    const ferryTicketCategories =
      ferryTicketCategoryDictionary.value[
        currentFerryTicketCategoryEditionKey.value
      ];

    assertValue(
      ferryTicketCategories,
      'upsellStore.ts > setUserSelectionFromModal > ferryTicketCategories'
    );

    const userSelection = ferryTicketCategories.find(
      (ferryTicketCategory: FerryTicketCategory) =>
        ferryTicketCategory.type === category
    );

    if (userSelection) {
      setUserSelectedFerryTicketCategory(
        currentFerryTicketCategoryEditionKey.value,
        userSelection
      );

      // Set previous user selected ticket category
      if (currentFerryTicketEditionIngredientId.value) {
        prevousSelectedTicketCategory.value[
          currentFerryTicketEditionIngredientId.value
        ] = category;
      }
    }

    currentFerryTicketEditionIngredientId.value = undefined;
    currentFerryTicketCategoryEditionKey.value = '';
  }

  function getUpsellTokens(
    tokenList: string[] = tokenListForAddons.value
  ): TokensForReplacement[] {
    const result: TokensForReplacement[] = [];

    tokenList.forEach((token) => {
      const hasAddon =
        userSelectedFerryTicketCategory.value[token] !== undefined;

      if (hasAddon) {
        const userSelectedAddonToken =
          userSelectedFerryTicketCategory.value[token];
        const ferryTicketCategories =
          ferryTicketCategoryDictionary.value[token];

        assertValue(
          ferryTicketCategories,
          'upsellStore.ts > getUpsellTokens > ferryTicketCategories'
        );

        const baseProduct = ferryTicketCategories[0];

        assertValue(
          userSelectedAddonToken,
          'upsellStore.ts > getUpsellTokens > userSelectedAddonToken'
        );
        assertValue(
          baseProduct,
          'upsellStore.ts > getUpsellTokens > baseProduct'
        );

        if (
          userSelectedAddonToken.price.amount - baseProduct.price.amount !==
          0
        ) {
          // INFO: this is how tokens are passed to checkout page. Ferry product is using Type=flight
          result.push({
            oldToken: `Type=flight&Identifier=${token}`,
            newToken: `Type=flight&Identifier=${userSelectedAddonToken.token}`,
          });
        }
      }
    });

    return result;
  }

  return {
    // Getters
    ferryTicketCategoryDictionary,
    userSelectedFerryTicketCategory,
    currentFerryTicketCategoryEditionKey,
    currentFerryTicketEditionIngredientId,
    isLoadingDictionary,

    // Actions
    setTicketCategoryToDictionary,
    fetchTicketCategories,
    getTicketCategories,
    setUserSelectionFromModal,
    getUpsellTokens,
  };
});
